import {
    Box,
    Text,
    Badge,
    Button,
    HStack,
    VStack,
    Flex,
    useColorModeValue,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Select,
    Input,
    Card,
    CardBody,
    Spinner,
    Center,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Checkbox,
    CheckboxGroup,
    Stack,
    Divider,
    Tabs,
    Tab,
    TabList,
    InputGroup,
    InputLeftElement,
    Switch,
    Radio,
    RadioGroup
} from '@chakra-ui/react';
import {
    MdPerson,
    MdApps,
    MdViewList,
    MdSearch
} from 'react-icons/md';
import { useState, useMemo } from 'react';

const ClientAppMappingModal = ({
    isOpen,
    onClose,
    mappingData,
    handleClientChange,
    handleApplicationSelection,
    handleBulkApplicationSelection,
    handleVersionChange,
    clients,
    availableApplications,
    mappingLoading,
    handleCreateMapping,
    handleCreateBulkMapping,
    mappingMode,
    setMappingMode,
    filteredApplications,
    mappingSearchTerm,
    setmappingSearchTerm
}) => {
    // Local state for version input method
    const [versionInputMethod, setVersionInputMethod] = useState('select'); // 'select' or 'manual'
    const [versionValidationError, setVersionValidationError] = useState('');
    // Tab colors
    const tabBg = useColorModeValue('gray.100', 'gray.700');
    const tabSelectedBg = useColorModeValue('white', 'gray.600');
    const tabBorderColor = useColorModeValue('gray.200', 'gray.600');

    // Card colors
    const cardBg = useColorModeValue('white', 'gray.700');
    const cardBorderColor = useColorModeValue('gray.200', 'gray.600');
    const cardHoverBg = useColorModeValue('blue.50', 'blue.900');
    const badgeVariant = useColorModeValue('subtle', 'solid');
    const modalBg = useColorModeValue('#FFFFFF', '#1A1B23');
    const modalBorderColor = useColorModeValue('#E2E4E9', '#2D3748');
    const modalHeaderGradient = useColorModeValue('linear-gradient(135deg, #1a365d 0%, #2c5282 100%)', 'linear-gradient(135deg, #0f1419 0%, #1a202c 100%)');
    const modalHeaderText = useColorModeValue('#FFFFFF', '#F7FAFC');
    const modalHeaderIconBg = useColorModeValue('rgba(255, 255, 255, 0.2)', 'rgba(255, 255, 255, 0.1)');
    const modalCloseButtonHover = useColorModeValue('rgba(255, 255, 255, 0.2)', 'rgba(255, 255, 255, 0.1)');
    const modalFooterBg = useColorModeValue('#FAFBFC', '#171923');

    const stepActiveColor = useColorModeValue('#2B6CB0', '#63B3ED');
    const stepInactiveColor = useColorModeValue('#CBD5E0', '#4A5568');
    const stepPendingColor = useColorModeValue('#E2E8F0', '#2D3748');
    const stepTextColor = useColorModeValue('#FFFFFF', '#1A202C');
    const stepPendingTextColor = useColorModeValue('#2B6CB0', '#63B3ED');
    const stepActiveLabelColor = useColorModeValue('#2B6CB0', '#63B3ED');
    const stepInactiveLabelColor = useColorModeValue('#A0AEC0', '#718096');
    const stepPendingLabelColor = useColorModeValue('#4A5568', '#A0AEC0');
    const stepConnectorActiveColor = useColorModeValue('#BEE3F8', '#2C5282');
    const stepConnectorInactiveColor = useColorModeValue('#E2E8F0', '#4A5568');

    // Form Input Colors
    const formLabelColor = useColorModeValue('#2D3748', '#E2E8F0');
    const formInputBg = useColorModeValue('#FFFFFF', '#2D3748');
    const formInputBorderColor = useColorModeValue('#D1D5DB', '#4A5568');
    const formInputTextColor = useColorModeValue('#1A202C', '#F7FAFC');
    const formInputHoverBorderColor = useColorModeValue('#3182CE', '#63B3ED');
    const formInputFocusBorderColor = useColorModeValue('#2B6CB0', '#4299E1');
    const formInputFocusShadowColor = useColorModeValue('rgba(66, 153, 225, 0.2)', 'rgba(66, 153, 225, 0.3)');
    const searchIconColor = useColorModeValue('#A0AEC0', '#718096');
    const searchPlaceholderColor = useColorModeValue('#A0AEC0', '#718096');
    
    // Alert Colors (Info)
    const alertInfoBg = useColorModeValue('#EBF4FF', '#1E2833');
    const alertInfoBorderColor = useColorModeValue('#3182CE', '#2B6CB0');
    const alertInfoIconColor = useColorModeValue('#3182CE', '#63B3ED');
    const alertInfoTitleColor = useColorModeValue('#1E3A8A', '#BEE3F8');
    const alertInfoDescColor = useColorModeValue('#1E40AF', '#90CDF4');

    // Application Preview Colors
    const appPreviewBg = useColorModeValue('linear-gradient(135deg, #EBF4FF 0%, #F0F4F8 100%)', 'linear-gradient(135deg, #1A202C 0%, #2D3748 100%)');
    const appPreviewBorderColor = useColorModeValue('#3182CE', '#4299E1');
    const appPreviewAccentColor = useColorModeValue('#3182CE', '#4299E1');
    const appPreviewTitleColor = useColorModeValue('#1E3A8A', '#BEE3F8');
    const appPreviewSubtitleColor = useColorModeValue('#4A5568', '#A0AEC0');
    const appPreviewLabelColor = useColorModeValue('#2D3748', '#E2E8F0');
    const appPreviewDescColor = useColorModeValue('#4A5568', '#CBD5E0');

    // Badge Colors
    const badgePurpleBg = useColorModeValue('#553C9A', '#7C3AED');
    const badgeGreenBg = useColorModeValue('#047857', '#10B981');
    const badgeRedBg = useColorModeValue('#DC2626', '#EF4444');
    const badgeBlueBg = useColorModeValue('#1E40AF', '#3B82F6');
    const badgeTextColor = useColorModeValue('#FFFFFF', '#FFFFFF');

    // Summary Section Colors
    const summarySectionBg = useColorModeValue('#F9FAFB', '#2D3748');
    const summarySectionBorderColor = useColorModeValue('#E5E7EB', '#4A5568');
    const summaryTextColor = useColorModeValue('#374151', '#E2E8F0');
    const summaryBadgeBg = useColorModeValue('#F3F4F6', '#4A5568');
    const summaryBadgeTextColor = useColorModeValue('#6B7280', '#A0AEC0');

    // Version Guidelines Colors
    const versionGuidelinesBg = useColorModeValue('#F8FAFC', '#2D3748');
    const versionGuidelinesBorderColor = useColorModeValue('#E2E8F0', '#4A5568');
    const versionGuidelinesTitleColor = useColorModeValue('#374151', '#E2E8F0');
    const versionGuidelinesTextColor = useColorModeValue('#6B7280', '#A0AEC0');

    // Version Preview Colors
    const versionPreviewBg = useColorModeValue('#F0FDF4', '#1F2937');
    const versionPreviewBorderColor = useColorModeValue('#10B981', '#059669');
    const versionPreviewTextColor = useColorModeValue('#047857', '#34D399');
    const versionBadgeBg = useColorModeValue('#059669', '#10B981');
    const versionBadgeTextColor = useColorModeValue('#FFFFFF', '#FFFFFF');

    // Button Colors
    const primaryButtonBg = useColorModeValue('#2563EB', '#3B82F6');
    const primaryButtonTextColor = useColorModeValue('#FFFFFF', '#FFFFFF');
    const primaryButtonHoverBg = useColorModeValue('#1D4ED8', '#2563EB');
    const primaryButtonDisabledBg = useColorModeValue('#D1D5DB', '#4A5568');
    const primaryButtonDisabledTextColor = useColorModeValue('#9CA3AF', '#A0AEC0');

    const cancelButtonTextColor = useColorModeValue('#6B7280', '#A0AEC0');
    const cancelButtonHoverBg = useColorModeValue('#F9FAFB', '#2D3748');

    // Loading States
    const loadingSpinnerColor = useColorModeValue('#3182CE', '#63B3ED');
    const loadingTextColor = useColorModeValue('#718096', '#A0AEC0');

    // Section Dividers
    const sectionDividerColor = useColorModeValue('#E2E8F0', '#4A5568');

    // Function to generate version options based on appVersion and minimalVersion
    const generateVersionOptions = (appVersion, minimalVersion) => {
        if (!appVersion || !minimalVersion) return [];

        const parseVersion = (version) => {
            const cleaned = version.toString().replace(/^v/, ''); // Remove 'v' prefix if present
            const parts = cleaned.split('.');
            return {
                major: parseInt(parts[0]) || 0,
                minor: parseInt(parts[1]) || 0,
                patch: parts.length >= 3 ? parseInt(parts[2]) || 0 : null,
                hasThreeParts: parts.length >= 3,
                original: version.toString()
            };
        };

        const formatVersion = (major, minor, patch, hasThreeParts) => {
            if (hasThreeParts && patch !== null) {
                return `${major}.${minor}.${patch}`;
            }
            return `${major}.${minor}`;
        };

        const current = parseVersion(appVersion);
        const minimal = parseVersion(minimalVersion);
        const versionsSet = new Set(); // Use Set to prevent duplicates
        
        // Add the current (latest) version first
        versionsSet.add(current.original);

        let currentMajor = current.major;
        let currentMinor = current.minor;
        let currentPatch = current.hasThreeParts ? current.patch : null;

        // Determine if we should use three-part versioning based on input format
        const useThreeParts = current.hasThreeParts || minimal.hasThreeParts;

        while (
            currentMajor > minimal.major ||
            (currentMajor === minimal.major && currentMinor > minimal.minor) ||
            (currentMajor === minimal.major && currentMinor === minimal.minor && 
             useThreeParts && currentPatch !== null && minimal.patch !== null && currentPatch > minimal.patch)
        ) {
            // Decrement version appropriately based on format
            if (useThreeParts && currentPatch !== null && currentPatch > 0) {
                currentPatch--;
            } else if (currentMinor > 0) {
                currentMinor--;
                if (useThreeParts) {
                    currentPatch = 9; // Reset patch to 9 when decrementing minor
                }
            } else if (currentMajor > 0) {
                currentMajor--;
                currentMinor = 9; // Reset minor to 9 when decrementing major
                if (useThreeParts) {
                    currentPatch = 9; // Reset patch to 9 when decrementing major
                }
            } else {
                break;
            }

            // Check if we've reached below the minimal version
            if (
                currentMajor < minimal.major ||
                (currentMajor === minimal.major && currentMinor < minimal.minor) ||
                (currentMajor === minimal.major && currentMinor === minimal.minor && 
                 useThreeParts && currentPatch !== null && minimal.patch !== null && currentPatch < minimal.patch)
            ) {
                break;
            }

            // Format version and add to set (automatically handles duplicates)
            const versionStr = formatVersion(currentMajor, currentMinor, currentPatch, useThreeParts);
            versionsSet.add(versionStr);
        }

        // Ensure minimal version is included if not already
        versionsSet.add(minimal.original);

        // Convert Set back to Array and return
        return Array.from(versionsSet);
    };

    // Function to compare version strings
    const compareVersions = (version1, version2) => {
        const parseVersion = (version) => {
            const cleaned = version.toString().replace(/^v/, '');
            const parts = cleaned.split('.');
            return {
                major: parseInt(parts[0]) || 0,
                minor: parseInt(parts[1]) || 0,
                patch: parseInt(parts[2]) || 0
            };
        };

        const v1 = parseVersion(version1);
        const v2 = parseVersion(version2);

        if (v1.major !== v2.major) {
            return v1.major - v2.major;
        }
        if (v1.minor !== v2.minor) {
            return v1.minor - v2.minor;
        }
        return v1.patch - v2.patch;
    };

    // Function to validate manual version input
    const validateManualVersion = (inputVersion, appVersion, minimalVersion) => {
        if (!inputVersion || !inputVersion.trim()) {
            return "Version is required";
        }

        const cleanedInput = inputVersion.trim();
        
        // Basic format validation (should contain at least one number and dot)
        const versionPattern = /^\d+(\.\d+)*$/;
        if (!versionPattern.test(cleanedInput)) {
            return "Version must be in format like '1.0', '2.1.3', etc.";
        }

        // Check if version is within valid range
        if (compareVersions(cleanedInput, minimalVersion) < 0) {
            return `Version must be greater than or equal to minimum version (${minimalVersion})`;
        }

        if (compareVersions(cleanedInput, appVersion) > 0) {
            return `Version must be less than or equal to latest version (${appVersion})`;
        }

        return null; // Valid version
    };

    // Handle version input method change
    const handleVersionInputMethodChange = (method) => {
        setVersionInputMethod(method);
        setVersionValidationError('');
        if (method === 'select') {
            // Clear any manual input and validation errors when switching to select
            if (!versionOptions.includes(mappingData.mappingVersion)) {
                handleVersionChange(''); // Reset to empty if current value isn't in dropdown
            }
        }
    };

    // Handle manual version input with validation
    const handleManualVersionChange = (inputValue) => {
        handleVersionChange(inputValue);
        if (selectedApp?.appVersion && selectedApp?.minimalVersion) {
            const error = validateManualVersion(inputValue, selectedApp.appVersion, selectedApp.minimalVersion);
            setVersionValidationError(error || '');
        }
    };

    // Loading States
    const getSingleMappingStep = () => {
        if (mappingData.mappingVersion) return 3;
        if (mappingData.selectedApplication) return 2;
        if (mappingData.selectedClientId) return 1;
        return 0;
    };

    // Get current step progress for bulk mapping
    const getBulkMappingStep = () => {
        if (mappingData.selectedApplications?.length > 0) return 2;
        if (mappingData.selectedClientId) return 1;
        return 0;
    };

    const currentStep = mappingMode === 'single' ? getSingleMappingStep() : getBulkMappingStep();
    const selectedApp = mappingMode === 'single' && mappingData.selectedApplication 
        ? availableApplications.find(app => app.appId.toString() === mappingData.selectedApplication)
        : null;

    // Generate version options for selected app
    const versionOptions = useMemo(() => {
        if (!selectedApp || !selectedApp.appVersion || !selectedApp.minimalVersion) {
            return [];
        }
        return generateVersionOptions(selectedApp.appVersion, selectedApp.minimalVersion);
    }, [selectedApp]);

    // Clear search when modal closes or mode changes
    const handleModalClose = () => {
        setmappingSearchTerm('');
        setVersionInputMethod('select');
        setVersionValidationError('');
        onClose();
    };

    const handleModeChange = (index) => {
        setmappingSearchTerm('');
        setVersionInputMethod('select');
        setVersionValidationError('');
        setMappingMode(index === 0 ? 'single' : 'bulk');
    };

    return (
        <Modal isOpen={isOpen} onClose={handleModalClose} size="3xl" isCentered>
            <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
            <ModalContent
                bg={modalBg}
                borderRadius="xl"
                boxShadow="2xl"
                border="2px solid"
                borderColor={modalBorderColor}
                mx={4}
                maxH="90vh"
            >
                <ModalHeader
                    bg={modalHeaderGradient}
                    color={modalHeaderText}
                    borderTopRadius="xl"
                    py={6}
                    px={8}
                >
                    <HStack spacing={3}>
                        <Box
                            p={2}
                            bg={modalHeaderIconBg}
                            borderRadius="lg"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            {mappingMode === 'single' ? <MdPerson size={20} /> : <MdApps size={20} />}
                        </Box>
                        <VStack align="start" spacing={0}>
                            <Text fontSize="xl" fontWeight="bold" lineHeight="1.2">
                                {mappingMode === 'single' ? 'Single Application Mapping' : 'Bulk Application Mapping'}
                            </Text>
                            <Text fontSize="sm" opacity={0.9} fontWeight="normal">
                                {mappingMode === 'single' 
                                    ? 'Link an application to a client with version control'
                                    : 'Map multiple applications to a client with latest versions'
                                }
                            </Text>
                        </VStack>
                    </HStack>
                </ModalHeader>

                <ModalCloseButton
                    color={modalHeaderText}
                    size="lg"
                    top={6}
                    right={6}
                    _hover={{ bg: modalCloseButtonHover }}
                />

                <ModalBody p={0} overflow="hidden" display="flex" flexDirection="column">
                    {/* Mode Selector Tabs */}
                    <Box bg={tabBg} px={8} pt={6} pb={4} borderBottom="1px" borderColor={tabBorderColor}>
                        <Tabs
                            variant="enclosed"
                            index={mappingMode === 'single' ? 0 : 1}
                            onChange={handleModeChange}
                        >
                            <TabList border="none" bg="transparent">
                                <Tab
                                    bg="transparent"
                                    color={formLabelColor}
                                    _selected={{
                                        bg: tabSelectedBg,
                                        color: formInputTextColor,
                                        borderColor: tabBorderColor,
                                        borderBottom: "1px solid",
                                        borderBottomColor: tabSelectedBg
                                    }}
                                    _hover={{
                                        bg: mappingMode === 'single' ? tabSelectedBg : 'whiteAlpha.200'
                                    }}
                                    borderRadius="lg"
                                    px={6}
                                    py={3}
                                    fontSize="md"
                                    fontWeight="semibold"
                                    mr={2}
                                >
                                    <HStack spacing={2}>
                                        <MdPerson />
                                        <Text>Single Mapping</Text>
                                    </HStack>
                                </Tab>
                                <Tab
                                    bg="transparent"
                                    color={formLabelColor}
                                    _selected={{
                                        bg: tabSelectedBg,
                                        color: formInputTextColor,
                                        borderColor: tabBorderColor,
                                        borderBottom: "1px solid",
                                        borderBottomColor: tabSelectedBg
                                    }}
                                    _hover={{
                                        bg: mappingMode === 'bulk' ? tabSelectedBg : 'whiteAlpha.200'
                                    }}
                                    borderRadius="lg"
                                    px={6}
                                    py={3}
                                    fontSize="md"
                                    fontWeight="semibold"
                                >
                                    <HStack spacing={2}>
                                        <MdViewList />
                                        <Text>Bulk Mapping</Text>
                                    </HStack>
                                </Tab>
                            </TabList>
                        </Tabs>
                    </Box>

                    {/* Scrollable Content Area */}
                    <Box 
                        flex="1" 
                        overflow="auto"
                        css={{
                            '&::-webkit-scrollbar': {
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'rgba(0,0,0,0.05)',
                                borderRadius: '10px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '10px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            },
                        }}
                    >
                        <VStack spacing={8} align="stretch" p={8}>
                            {/* Step Indicator */}
                            {mappingMode === 'single' ? (
                                <HStack spacing={3} justify="center">
                                    <HStack spacing={2}>
                                        <Box
                                            w={8} h={8}
                                            bg={currentStep >= 1 ? stepActiveColor : stepInactiveColor}
                                            color={stepTextColor}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontSize="sm"
                                            fontWeight="bold"
                                            transition="all 0.2s"
                                        >
                                            1
                                        </Box>
                                        <Text fontSize="sm" fontWeight="medium" color={currentStep >= 1 ? stepActiveLabelColor : stepInactiveLabelColor}>
                                            Select Client
                                        </Text>
                                    </HStack>

                                    <Box w={6} h={0.5} bg={currentStep >= 1 ? stepConnectorActiveColor : stepConnectorInactiveColor} />

                                    <HStack spacing={2}>
                                        <Box
                                            w={8} h={8}
                                            bg={currentStep >= 2 ? stepActiveColor : currentStep >= 1 ? stepPendingColor : stepInactiveColor}
                                            color={currentStep >= 2 ? stepTextColor : currentStep >= 1 ? stepPendingTextColor : stepTextColor}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontSize="sm"
                                            fontWeight="bold"
                                            transition="all 0.2s"
                                        >
                                            2
                                        </Box>
                                        <Text
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color={currentStep >= 2 ? stepActiveLabelColor : currentStep >= 1 ? stepPendingLabelColor : stepInactiveLabelColor}
                                        >
                                            Select Application
                                        </Text>
                                    </HStack>

                                    <Box w={6} h={0.5} bg={currentStep >= 2 ? stepConnectorActiveColor : stepConnectorInactiveColor} />

                                    <HStack spacing={2}>
                                        <Box
                                            w={8} h={8}
                                            bg={currentStep >= 3 ? stepActiveColor : currentStep >= 2 ? stepPendingColor : stepInactiveColor}
                                            color={currentStep >= 3 ? stepTextColor : currentStep >= 2 ? stepPendingTextColor : stepTextColor}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontSize="sm"
                                            fontWeight="bold"
                                            transition="all 0.2s"
                                        >
                                            3
                                        </Box>
                                        <Text
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color={currentStep >= 3 ? stepActiveLabelColor : currentStep >= 2 ? stepPendingLabelColor : stepInactiveLabelColor}
                                        >
                                            Version
                                        </Text>
                                    </HStack>
                                </HStack>
                            ) : (
                                <HStack spacing={3} justify="center">
                                    <HStack spacing={2}>
                                        <Box
                                            w={8} h={8}
                                            bg={currentStep >= 1 ? stepActiveColor : stepInactiveColor}
                                            color={stepTextColor}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontSize="sm"
                                            fontWeight="bold"
                                            transition="all 0.2s"
                                        >
                                            1
                                        </Box>
                                        <Text fontSize="sm" fontWeight="medium" color={currentStep >= 1 ? stepActiveLabelColor : stepInactiveLabelColor}>
                                            Select Client
                                        </Text>
                                    </HStack>

                                    <Box w={6} h={0.5} bg={currentStep >= 1 ? stepConnectorActiveColor : stepConnectorInactiveColor} />

                                    <HStack spacing={2}>
                                        <Box
                                            w={8} h={8}
                                            bg={currentStep >= 2 ? stepActiveColor : currentStep >= 1 ? stepPendingColor : stepInactiveColor}
                                            color={currentStep >= 2 ? stepTextColor : currentStep >= 1 ? stepPendingTextColor : stepTextColor}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontSize="sm"
                                            fontWeight="bold"
                                            transition="all 0.2s"
                                        >
                                            2
                                        </Box>
                                        <Text
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color={currentStep >= 2 ? stepActiveLabelColor : currentStep >= 1 ? stepPendingLabelColor : stepInactiveLabelColor}
                                        >
                                            Select Applications
                                        </Text>
                                    </HStack>

                                    <Box w={6} h={0.5} bg={currentStep >= 2 ? stepConnectorActiveColor : stepConnectorInactiveColor} />

                                    <HStack spacing={2}>
                                        <Box
                                            w={8} h={8}
                                            bg={currentStep >= 2 ? stepActiveColor : stepInactiveColor}
                                            color={stepTextColor}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            fontSize="sm"
                                            fontWeight="bold"
                                            transition="all 0.2s"
                                        >
                                            3
                                        </Box>
                                        <Text
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color={currentStep >= 2 ? stepActiveLabelColor : stepInactiveLabelColor}
                                        >
                                            Latest Versions
                                        </Text>
                                    </HStack>
                                </HStack>
                            )}

                            {/* Client Selection */}
                            <Box>
                                <FormControl isRequired>
                                    <FormLabel
                                        color={formLabelColor}
                                        fontSize="md"
                                        fontWeight="semibold"
                                        mb={3}
                                    >
                                        Client Selection
                                    </FormLabel>
                                    <Box position="relative">
                                        <Select
                                            placeholder="Choose a client to map applications to..."
                                            value={mappingData.selectedClientId}
                                            onChange={(e) => handleClientChange(e.target.value)}
                                            bg={formInputBg}
                                            borderColor={formInputBorderColor}
                                            color={formInputTextColor}
                                            size="lg"
                                            borderRadius="lg"
                                            _hover={{
                                                borderColor: formInputHoverBorderColor,
                                                boxShadow: `0 0 0 1px ${formInputHoverBorderColor}`
                                            }}
                                            _focus={{
                                                borderColor: formInputFocusBorderColor,
                                                boxShadow: `0 0 0 3px ${formInputFocusShadowColor}`
                                            }}
                                            isDisabled={mappingLoading}
                                        >
                                            {clients.map(client => (
                                                <option key={client.clientId} value={client.clientId}>
                                                    {client.clientName}
                                                </option>
                                            ))}
                                        </Select>
                                        {mappingLoading && (
                                            <Box position="absolute" right={12} top="50%" transform="translateY(-50%)">
                                                <Spinner size="sm" color={loadingSpinnerColor} />
                                            </Box>
                                        )}
                                    </Box>
                                    {mappingLoading && (
                                        <Text fontSize="sm" color={loadingTextColor} mt={2} fontStyle="italic">
                                            Loading clients...
                                        </Text>
                                    )}
                                </FormControl>
                            </Box>

                            {/* Application Selection */}
                            {mappingData.selectedClientId && (
                                <>
                                    <Divider borderColor={sectionDividerColor} />

                                    <Box>
                                        <FormControl isRequired>
                                            <FormLabel
                                                color={formLabelColor}
                                                fontSize="md"
                                                fontWeight="semibold"
                                                mb={3}
                                            >
                                                {mappingMode === 'single' ? 'Application Selection' : 'Applications Selection'}
                                            </FormLabel>

                                            {mappingLoading ? (
                                                <Center py={12}>
                                                    <VStack spacing={4}>
                                                        <Spinner size="xl" color={loadingSpinnerColor} thickness="4px" />
                                                        <Text fontSize="md" color={loadingTextColor} fontWeight="medium">
                                                            Loading available applications...
                                                        </Text>
                                                    </VStack>
                                                </Center>
                                            ) : availableApplications.length === 0 ? (
                                                <Alert
                                                    status="info"
                                                    bg={alertInfoBg}
                                                    borderColor={alertInfoBorderColor}
                                                    borderWidth="2px"
                                                    borderRadius="lg"
                                                    p={6}
                                                >
                                                    <AlertIcon color={alertInfoIconColor} />
                                                    <Box>
                                                        <AlertTitle fontSize="md" color={alertInfoTitleColor} fontWeight="semibold">
                                                            No Applications Available
                                                        </AlertTitle>
                                                        <AlertDescription fontSize="sm" color={alertInfoDescColor} mt={1}>
                                                            All applications are already mapped to this client, or no applications exist in the system.
                                                        </AlertDescription>
                                                    </Box>
                                                </Alert>
                                            ) : (
                                                <VStack spacing={6} align="stretch">
                                                    {/* Search Input */}
                                                    <InputGroup size="lg">
                                                        <InputLeftElement pointerEvents="none">
                                                            <MdSearch color={searchIconColor} size={20} />
                                                        </InputLeftElement>
                                                        <Input
                                                            placeholder="Search applications by name, type, version, or ID..."
                                                            value={mappingSearchTerm}
                                                            onChange={(e) => setmappingSearchTerm(e.target.value)}
                                                            bg={formInputBg}
                                                            borderColor={formInputBorderColor}
                                                            color={formInputTextColor}
                                                            borderRadius="lg"
                                                            _placeholder={{ color: searchPlaceholderColor }}
                                                            _hover={{
                                                                borderColor: formInputHoverBorderColor,
                                                                boxShadow: `0 0 0 1px ${formInputHoverBorderColor}`
                                                            }}
                                                            _focus={{
                                                                borderColor: formInputFocusBorderColor,
                                                                boxShadow: `0 0 0 3px ${formInputFocusShadowColor}`
                                                            }}
                                                        />
                                                    </InputGroup>

                                                    {/* Search Results Info */}
                                                    {mappingSearchTerm.trim() && (
                                                        <Box
                                                            bg={alertInfoBg}
                                                            borderWidth="1px"
                                                            borderColor={alertInfoBorderColor}
                                                            borderRadius="md"
                                                            px={4}
                                                            py={2}
                                                        >
                                                            <Text fontSize="sm" color={alertInfoTitleColor}>
                                                                Found {filteredApplications.length} application{filteredApplications.length !== 1 ? 's' : ''} 
                                                                {filteredApplications.length !== availableApplications.length && 
                                                                    ` of ${availableApplications.length} total`
                                                                }
                                                                {mappingSearchTerm.trim() && ` matching "${mappingSearchTerm}"`}
                                                            </Text>
                                                        </Box>
                                                    )}

                                                    {filteredApplications.length === 0 && mappingSearchTerm.trim() ? (
                                                        <Alert
                                                            status="info"
                                                            bg={alertInfoBg}
                                                            borderColor={alertInfoBorderColor}
                                                            borderWidth="2px"
                                                            borderRadius="lg"
                                                            p={6}
                                                        >
                                                            <AlertIcon color={alertInfoIconColor} />
                                                            <Box>
                                                                <AlertTitle fontSize="md" color={alertInfoTitleColor} fontWeight="semibold">
                                                                    No Applications Found
                                                                </AlertTitle>
                                                                <AlertDescription fontSize="sm" color={alertInfoDescColor} mt={1}>
                                                                    No applications match your search term "{mappingSearchTerm}". Try adjusting your search or clear the filter to see all available applications.
                                                                </AlertDescription>
                                                            </Box>
                                                        </Alert>
                                                    ) : mappingMode === 'single' ? (
                                                        <>
                                                            {/* Single Application Dropdown */}
                                                            <Select
                                                                placeholder={filteredApplications.length === 0 ? "No applications match your search..." : "Choose an application to map..."}
                                                                value={mappingData.selectedApplication}
                                                                onChange={(e) => handleApplicationSelection(e.target.value)}
                                                                bg={formInputBg}
                                                                borderColor={formInputBorderColor}
                                                                color={formInputTextColor}
                                                                size="lg"
                                                                borderRadius="lg"
                                                                _hover={{
                                                                    borderColor: formInputHoverBorderColor,
                                                                    boxShadow: `0 0 0 1px ${formInputHoverBorderColor}`
                                                                }}
                                                                _focus={{
                                                                    borderColor: formInputFocusBorderColor,
                                                                    boxShadow: `0 0 0 3px ${formInputFocusShadowColor}`
                                                                }}
                                                                isDisabled={filteredApplications.length === 0}
                                                            >
                                                                {filteredApplications.map((app) => (
                                                                    <option key={app.appId} value={app.appId.toString()}>
                                                                        {app.appName} • {app.appType} • {app.appVersion}
                                                                    </option>
                                                                ))}
                                                            </Select>

                                                            {/* Selected Application Preview */}
                                                            {selectedApp && (
                                                                <Box
                                                                    bg={appPreviewBg}
                                                                    borderWidth="2px"
                                                                    borderColor={appPreviewBorderColor}
                                                                    borderRadius="xl"
                                                                    p={6}
                                                                    position="relative"
                                                                    overflow="hidden"
                                                                >
                                                                    <Box
                                                                        position="absolute"
                                                                        top={-2}
                                                                        right={-2}
                                                                        w={24}
                                                                        h={24}
                                                                        bg={appPreviewAccentColor}
                                                                        borderRadius="full"
                                                                        opacity={0.1}
                                                                    />

                                                                    <VStack align="start" spacing={4} position="relative">
                                                                        <HStack justify="space-between" w="full">
                                                                            <VStack align="start" spacing={2}>
                                                                                <Text fontSize="xl" fontWeight="bold" color={appPreviewTitleColor}>
                                                                                    {selectedApp.appName}
                                                                                </Text>
                                                                                <Text fontSize="sm" color={appPreviewSubtitleColor} fontWeight="medium">
                                                                                    Application ID: {selectedApp.appId}
                                                                                </Text>
                                                                            </VStack>
                                                                            <HStack spacing={2}>
                                                                                <Badge
                                                                                    variant="solid"
                                                                                    colorScheme="purple"
                                                                                    fontSize="xs"
                                                                                    px={3}
                                                                                    py={1}
                                                                                    borderRadius="full"
                                                                                    fontWeight="semibold"
                                                                                >
                                                                                    {selectedApp.appType}
                                                                                </Badge>
                                                                                <Badge
                                                                                    variant="solid"
                                                                                    colorScheme={selectedApp.appStatus ? 'green' : 'red'}
                                                                                    fontSize="xs"
                                                                                    px={3}
                                                                                    py={1}
                                                                                    borderRadius="full"
                                                                                    fontWeight="semibold"
                                                                                >
                                                                                    {selectedApp.appStatus ? 'ACTIVE' : 'INACTIVE'}
                                                                                </Badge>
                                                                                <Badge
                                                                                    variant="solid"
                                                                                    colorScheme="blue"
                                                                                    fontSize="xs"
                                                                                    px={3}
                                                                                    py={1}
                                                                                    borderRadius="full"
                                                                                    fontWeight="semibold"
                                                                                >
                                                                                    {selectedApp.appVersion}
                                                                                </Badge>
                                                                            </HStack>
                                                                        </HStack>

                                                                        {/* Version Range Info */}
                                                                        <Box
                                                                            bg="whiteAlpha.500"
                                                                            p={3}
                                                                            borderRadius="md"
                                                                            w="full"
                                                                        >
                                                                            <HStack spacing={4} justify="space-between">
                                                                                <VStack align="start" spacing={1}>
                                                                                    <Text fontSize="xs" color={appPreviewTitleColor} fontWeight="medium">
                                                                                        Latest Version
                                                                                    </Text>
                                                                                    <Badge variant="solid" colorScheme="green" fontSize="xs">
                                                                                        {selectedApp.appVersion}
                                                                                    </Badge>
                                                                                </VStack>
                                                                                <VStack align="start" spacing={1}>
                                                                                    <Text fontSize="xs" color={appPreviewTitleColor} fontWeight="medium">
                                                                                        Minimum Version
                                                                                    </Text>
                                                                                    <Badge variant="solid" colorScheme="orange" fontSize="xs">
                                                                                        {selectedApp.minimalVersion}
                                                                                    </Badge>
                                                                                </VStack>
                                                                                <VStack align="start" spacing={1}>
                                                                                    <Text fontSize="xs" color={appPreviewTitleColor} fontWeight="medium">
                                                                                        Available Versions
                                                                                    </Text>
                                                                                    <Badge variant="solid" colorScheme="blue" fontSize="xs">
                                                                                        {versionOptions.length} options
                                                                                    </Badge>
                                                                                </VStack>
                                                                            </HStack>
                                                                        </Box>

                                                                        {selectedApp.appDescription && (
                                                                            <Box>
                                                                                <Text fontSize="sm" fontWeight="semibold" color={appPreviewTitleColor} mb={2}>
                                                                                    Description
                                                                                </Text>
                                                                                <Text fontSize="sm" color={appPreviewSubtitleColor} lineHeight="1.6">
                                                                                    {selectedApp.appDescription}
                                                                                </Text>
                                                                            </Box>
                                                                        )}
                                                                    </VStack>
                                                                </Box>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {/* Bulk Applications List */}
                                                            <Box
                                                                maxH="400px"
                                                                overflowY="auto"
                                                                borderWidth="2px"
                                                                borderColor={formInputBorderColor}
                                                                borderRadius="lg"
                                                                p={4}
                                                                bg={formInputBg}
                                                                css={{
                                                                    '&::-webkit-scrollbar': {
                                                                        width: '8px',
                                                                    },
                                                                    '&::-webkit-scrollbar-track': {
                                                                        background: 'rgba(0,0,0,0.05)',
                                                                        borderRadius: '10px',
                                                                    },
                                                                    '&::-webkit-scrollbar-thumb': {
                                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                                        borderRadius: '10px',
                                                                    },
                                                                    '&::-webkit-scrollbar-thumb:hover': {
                                                                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                                                    },
                                                                }}
                                                            >
                                                                <CheckboxGroup
                                                                    value={mappingData.selectedApplications}
                                                                    onChange={handleBulkApplicationSelection}
                                                                >
                                                                    <Stack spacing={4}>
                                                                        {filteredApplications.map((app) => (
                                                                            <Card
                                                                                key={app.appId}
                                                                                size="sm"
                                                                                bg={cardBg}
                                                                                borderWidth="2px"
                                                                                borderColor={cardBorderColor}
                                                                                borderRadius="lg"
                                                                                transition="all 0.2s"
                                                                                _hover={{ 
                                                                                    bg: cardHoverBg,
                                                                                    borderColor: formInputHoverBorderColor,
                                                                                    transform: "translateY(-1px)",
                                                                                    boxShadow: "md"
                                                                                }}
                                                                            >
                                                                                <CardBody p={4}>
                                                                                    <Checkbox 
                                                                                        value={app.appId.toString()} 
                                                                                        colorScheme="blue"
                                                                                        size="lg"
                                                                                    >
                                                                                        <VStack align="start" spacing={3} ml={3}>
                                                                                            <HStack spacing={4} align="center" w="full" justify="space-between">
                                                                                                <VStack align="start" spacing={1}>
                                                                                                    <Text fontWeight="bold" fontSize="md" color={formLabelColor}>
                                                                                                        {app.appName}
                                                                                                    </Text>
                                                                                                    <Text fontSize="xs" color={loadingTextColor}>
                                                                                                        ID: {app.appId}
                                                                                                    </Text>
                                                                                                </VStack>
                                                                                                
                                                                                                <HStack spacing={2}>
                                                                                                    <Badge 
                                                                                                        colorScheme="purple" 
                                                                                                        variant={badgeVariant}
                                                                                                        fontSize="xs"
                                                                                                        px={2}
                                                                                                        py={1}
                                                                                                        borderRadius="full"
                                                                                                        fontWeight="semibold"
                                                                                                    >
                                                                                                        {app.appType}
                                                                                                    </Badge>
                                                                                                    <Badge 
                                                                                                        colorScheme={app.appStatus ? 'green' : 'red'}
                                                                                                        variant={badgeVariant}
                                                                                                        fontSize="xs"
                                                                                                        px={2}
                                                                                                        py={1}
                                                                                                        borderRadius="full"
                                                                                                        fontWeight="semibold"
                                                                                                    >
                                                                                                        {app.appStatus ? 'ACTIVE' : 'INACTIVE'}
                                                                                                    </Badge>
                                                                                                    <Badge 
                                                                                                        colorScheme="blue"
                                                                                                        variant="solid"
                                                                                                        fontSize="xs"
                                                                                                        px={2}
                                                                                                        py={1}
                                                                                                        borderRadius="full"
                                                                                                        fontWeight="semibold"
                                                                                                    >
                                                                                                        {app.appVersion} → Latest
                                                                                                    </Badge>
                                                                                                </HStack>
                                                                                            </HStack>
                                                                                            
                                                                                            {app.appDescription && (
                                                                                                <Text fontSize="sm" color={loadingTextColor} lineHeight="1.5">
                                                                                                    {app.appDescription}
                                                                                                </Text>
                                                                                            )}
                                                                                        </VStack>
                                                                                    </Checkbox>
                                                                                </CardBody>
                                                                            </Card>
                                                                        ))}
                                                                    </Stack>
                                                                </CheckboxGroup>
                                                            </Box>
                                                            
                                                            {/* Summary Section */}
                                                            <Box
                                                                bg={summarySectionBg}
                                                                p={4}
                                                                borderRadius="lg"
                                                                borderWidth="2px"
                                                                borderColor={summarySectionBorderColor}
                                                            >
                                                                <HStack justify="space-between" mb={2}>
                                                                    <Text fontSize="md" color={summaryTextColor} fontWeight="semibold">
                                                                        Mapping Summary
                                                                    </Text>
                                                                    <Badge
                                                                        variant="subtle"
                                                                        fontSize="sm"
                                                                        bg={summaryBadgeBg}
                                                                        color={summaryBadgeTextColor}
                                                                        px={3}
                                                                        py={1}
                                                                        borderRadius="full"
                                                                        fontWeight="bold"
                                                                    >
                                                                        {mappingData.selectedApplications?.length || 0} selected
                                                                    </Badge>
                                                                </HStack>
                                                                <VStack spacing={1} align="start">
                                                                    <Text fontSize="sm" color={loadingTextColor}>
                                                                        • Available applications: {filteredApplications.length}
                                                                        {mappingSearchTerm.trim() && filteredApplications.length !== availableApplications.length && 
                                                                            ` (filtered from ${availableApplications.length})`
                                                                        }
                                                                    </Text>
                                                                    <Text fontSize="sm" color={loadingTextColor}>
                                                                        • Selected for mapping: {mappingData.selectedApplications?.length || 0}
                                                                    </Text>
                                                                    <Text fontSize="sm" color={loadingTextColor}>
                                                                        • Default version: Latest (automatically applied)
                                                                    </Text>
                                                                </VStack>
                                                            </Box>
                                                        </>
                                                    )}
                                                </VStack>
                                            )}
                                        </FormControl>
                                    </Box>
                                </>
                            )}

                            {/* Version Section - Only for Single Mode */}
                            {mappingMode === 'single' && mappingData.selectedApplication && (
                                <>
                                    <Divider borderColor={sectionDividerColor} />

                                    <Box>
                                        <FormControl isRequired isInvalid={!!versionValidationError}>
                                            <FormLabel
                                                color={formLabelColor}
                                                fontSize="md"
                                                fontWeight="semibold"
                                                mb={3}
                                            >
                                                Mapping Version
                                            </FormLabel>
                                            <VStack spacing={4} align="stretch">
                                                {/* Version Input Method Selector */}
                                                <Box
                                                    bg={versionGuidelinesBg}
                                                    p={4}
                                                    borderRadius="lg"
                                                    borderWidth="1px"
                                                    borderColor={versionGuidelinesBorderColor}
                                                >
                                                    <Text fontSize="sm" color={versionGuidelinesTitleColor} fontWeight="medium" mb={3}>
                                                        Choose Version Input Method:
                                                    </Text>
                                                    <RadioGroup 
                                                        value={versionInputMethod} 
                                                        onChange={handleVersionInputMethodChange}
                                                    >
                                                        <Stack spacing={3}>
                                                            <Radio value="select" colorScheme="blue" size="md">
                                                                <VStack align="start" spacing={1} ml={2}>
                                                                    <Text fontSize="sm" fontWeight="medium" color={formLabelColor}>
                                                                        Select from available versions
                                                                    </Text>
                                                                    <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                                        Choose from {versionOptions.length} pre-defined versions between {selectedApp?.minimalVersion} and {selectedApp?.appVersion}
                                                                    </Text>
                                                                </VStack>
                                                            </Radio>
                                                            <Radio value="manual" colorScheme="blue" size="md">
                                                                <VStack align="start" spacing={1} ml={2}>
                                                                    <Text fontSize="sm" fontWeight="medium" color={formLabelColor}>
                                                                        Enter version manually
                                                                    </Text>
                                                                    <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                                        Input a custom version within the supported range
                                                                    </Text>
                                                                </VStack>
                                                            </Radio>
                                                        </Stack>
                                                    </RadioGroup>
                                                </Box>

                                                {/* Version Input Field */}
                                                {versionInputMethod === 'select' ? (
                                                    // Version Selector Dropdown
                                                    <Select
                                                        placeholder="Select a version for mapping..."
                                                        value={mappingData.mappingVersion || ''}
                                                        onChange={(e) => {
                                                            handleVersionChange(e.target.value);
                                                            setVersionValidationError('');
                                                        }}
                                                        bg={formInputBg}
                                                        borderColor={formInputBorderColor}
                                                        color={formInputTextColor}
                                                        size="lg"
                                                        borderRadius="lg"
                                                        _hover={{
                                                            borderColor: formInputHoverBorderColor,
                                                            boxShadow: `0 0 0 1px ${formInputHoverBorderColor}`
                                                        }}
                                                        _focus={{
                                                            borderColor: formInputFocusBorderColor,
                                                            boxShadow: `0 0 0 3px ${formInputFocusShadowColor}`
                                                        }}
                                                        isDisabled={versionOptions.length === 0}
                                                    >
                                                        {versionOptions.map((version, index) => (
                                                            <option key={version} value={version}>
                                                                {version}
                                                                {index === 0 && ' (Latest)'}
                                                                {index === versionOptions.length - 1 && versionOptions.length > 1 && ' (Minimum)'}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    // Manual Version Input
                                                    <Input
                                                        placeholder={`Enter version between ${selectedApp?.minimalVersion} and ${selectedApp?.appVersion}...`}
                                                        value={mappingData.mappingVersion || ''}
                                                        onChange={(e) => handleManualVersionChange(e.target.value)}
                                                        bg={formInputBg}
                                                        borderColor={versionValidationError ? 'red.300' : formInputBorderColor}
                                                        color={formInputTextColor}
                                                        size="lg"
                                                        borderRadius="lg"
                                                        _hover={{
                                                            borderColor: versionValidationError ? 'red.300' : formInputHoverBorderColor,
                                                            boxShadow: versionValidationError ? '0 0 0 1px red' : `0 0 0 1px ${formInputHoverBorderColor}`
                                                        }}
                                                        _focus={{
                                                            borderColor: versionValidationError ? 'red.500' : formInputFocusBorderColor,
                                                            boxShadow: versionValidationError ? '0 0 0 3px rgba(255, 0, 0, 0.2)' : `0 0 0 3px ${formInputFocusShadowColor}`
                                                        }}
                                                        maxLength={20}
                                                    />
                                                )}

                                                {/* Error Message */}
                                                {versionValidationError && (
                                                    <FormErrorMessage fontSize="sm" mt={1}>
                                                        {versionValidationError}
                                                    </FormErrorMessage>
                                                )}

                                                {/* Version Guidelines */}
                                                <Box
                                                    bg={versionGuidelinesBg}
                                                    p={4}
                                                    borderRadius="lg"
                                                    borderWidth="1px"
                                                    borderColor={versionGuidelinesBorderColor}
                                                >
                                                    <Text fontSize="sm" color={versionGuidelinesTitleColor} fontWeight="medium" mb={2}>
                                                        Version Guidelines:
                                                    </Text>
                                                    <VStack spacing={1} align="start">
                                                        <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                            • Latest version ({selectedApp?.appVersion}) recommended for new mappings
                                                        </Text>
                                                        <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                            • Minimum supported version: {selectedApp?.minimalVersion}
                                                        </Text>
                                                        <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                            • {versionInputMethod === 'manual' ? 'Use format like 1.0, 2.1.3, etc.' : 'Choose appropriate version based on client requirements'}
                                                        </Text>
                                                        {versionInputMethod === 'manual' && (
                                                            <Text fontSize="xs" color={versionGuidelinesTextColor}>
                                                                • Version will be validated to ensure it's within the supported range
                                                            </Text>
                                                        )}
                                                    </VStack>
                                                </Box>

                                                {/* Version Preview */}
                                                {mappingData.mappingVersion && !versionValidationError && (
                                                    <Box
                                                        bg={versionPreviewBg}
                                                        borderWidth="2px"
                                                        borderColor={versionPreviewBorderColor}
                                                        borderRadius="lg"
                                                        p={4}
                                                    >
                                                        <HStack spacing={3} justify="space-between">
                                                            <HStack spacing={3}>
                                                                <Badge
                                                                    variant="solid"
                                                                    fontSize="sm"
                                                                    px={3}
                                                                    py={1}
                                                                    borderRadius="md"
                                                                    bg={versionBadgeBg}
                                                                    color={versionBadgeTextColor}
                                                                >
                                                                    {mappingData.mappingVersion}
                                                                </Badge>
                                                                <Text fontSize="sm" color={versionPreviewTextColor} fontWeight="medium">
                                                                    Version ready for mapping
                                                                </Text>
                                                            </HStack>
                                                            <HStack spacing={2}>
                                                                {mappingData.mappingVersion === selectedApp?.appVersion && (
                                                                    <Badge variant="solid" colorScheme="green" fontSize="xs">
                                                                        Latest
                                                                    </Badge>
                                                                )}
                                                                {mappingData.mappingVersion === selectedApp?.minimalVersion && (
                                                                    <Badge variant="solid" colorScheme="orange" fontSize="xs">
                                                                        Minimum
                                                                    </Badge>
                                                                )}
                                                                {versionInputMethod === 'manual' && 
                                                                 mappingData.mappingVersion !== selectedApp?.appVersion && 
                                                                 mappingData.mappingVersion !== selectedApp?.minimalVersion && (
                                                                    <Badge variant="solid" colorScheme="purple" fontSize="xs">
                                                                        Custom
                                                                    </Badge>
                                                                )}
                                                            </HStack>
                                                        </HStack>
                                                    </Box>
                                                )}
                                            </VStack>
                                        </FormControl>
                                    </Box>
                                </>
                            )}

                            {/* Latest Version Info - Only for Bulk Mode */}
                            {mappingMode === 'bulk' && mappingData.selectedApplications?.length > 0 && (
                                <>
                                    <Divider borderColor={sectionDividerColor} />
                                    
                                    <Box
                                        bg={alertInfoBg}
                                        borderWidth="2px"
                                        borderColor={alertInfoBorderColor}
                                        borderRadius="lg"
                                        p={6}
                                    >
                                        <VStack spacing={3} align="start">
                                            <HStack spacing={2}>
                                                <Box
                                                    w={6} h={6}
                                                    bg={stepActiveColor}
                                                    borderRadius="full"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                >
                                                    <Text fontSize="xs" color="white" fontWeight="bold">✓</Text>
                                                </Box>
                                                <Text fontSize="md" color={alertInfoTitleColor} fontWeight="semibold">
                                                    Latest Version Mapping
                                                </Text>
                                            </HStack>
                                            <Text fontSize="sm" color={alertInfoDescColor} lineHeight="1.6">
                                                All selected applications will be mapped with their latest available versions. 
                                                This ensures optimal compatibility and access to the newest features.
                                            </Text>
                                            <Box
                                                bg="whiteAlpha.500"
                                                p={3}
                                                borderRadius="md"
                                                w="full"
                                            >
                                                <Text fontSize="xs" color={alertInfoTitleColor} fontWeight="medium">
                                                    Note: You can update individual mapping versions later from the client management interface.
                                                </Text>
                                            </Box>
                                        </VStack>
                                    </Box>
                                </>
                            )}
                        </VStack>
                    </Box>
                </ModalBody>

                <ModalFooter bg={modalFooterBg} borderBottomRadius="xl" p={8} flexShrink={0}>
                    <HStack spacing={4} w="full" justify="end">
                        <Button
                            variant="ghost"
                            onClick={handleModalClose}
                            color={cancelButtonTextColor}
                            size="lg"
                            isDisabled={mappingLoading}
                            _hover={{ bg: cancelButtonHoverBg }}
                            px={8}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={mappingMode === 'single' ? handleCreateMapping : handleCreateBulkMapping}
                            isDisabled={
                                !mappingData.selectedClientId || 
                                mappingLoading ||
                                (mappingMode === 'single' 
                                    ? (!mappingData.selectedApplication || !mappingData.mappingVersion || !!versionValidationError)
                                    : (!mappingData.selectedApplications?.length)
                                )
                            }
                            isLoading={mappingLoading}
                            loadingText={mappingMode === 'single' ? "Creating Mapping..." : "Creating Mappings..."}
                            size="lg"
                            px={8}
                            fontWeight="semibold"
                            bg={primaryButtonBg}
                            color={primaryButtonTextColor}
                            _hover={{
                                bg: primaryButtonHoverBg,
                                transform: "translateY(-1px)",
                                boxShadow: "lg"
                            }}
                            _disabled={{
                                bg: primaryButtonDisabledBg,
                                color: primaryButtonDisabledTextColor
                            }}
                            transition="all 0.2s"
                        >
                            {mappingMode === 'single' 
                                ? 'Create Mapping' 
                                : `Create ${mappingData.selectedApplications?.length || 0} Mappings`
                            }
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ClientAppMappingModal;